import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import {
  useSubscription,
  useUpgradeSubscription,
  useCancelSubscription,
  useResumeSubscription,
} from '../../../hooks/useSubscription';

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('vi-VN');
}

export function SubscriptionTab() {
  const { t } = useTranslation('billing');
  const { data, isLoading, error } = useSubscription();
  const upgrade = useUpgradeSubscription();
  const cancel = useCancelSubscription();
  const resume = useResumeSubscription();
  const navigate = useNavigate();

  if (isLoading) return <div className="p-6 text-surface-500">…</div>;
  if (error || !data) {
    return (
      <div className="p-6 text-red-700 bg-red-50 border border-red-200 rounded-md max-w-2xl">
        {(error as Error | null)?.message ?? 'Không tải được thông tin gói. Kiểm tra API server đã chạy chưa.'}
      </div>
    );
  }

  const handleUpgrade = async (code: 'PRO' | 'BUSINESS') => {
    const res = await upgrade.mutateAsync(code);
    navigate(`/billing/invoices/${res.subscriptionInvoiceId}`);
  };

  const code = data.plan.code;
  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div className="border border-surface-200 rounded-lg p-5 bg-surface-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">{data.plan.name}</h2>
          {code !== 'FREE' && (
            <span className="text-[12px] font-medium px-2 py-0.5 rounded bg-brand-50 text-brand-700">
              {t(`status.${data.status}`)}
            </span>
          )}
        </div>
        {code !== 'FREE' && (
          <p className="text-[13px] text-surface-500">
            {data.autoRenew
              ? t('periodEnds', { date: fmtDate(data.currentPeriodEnd) })
              : t('autoRenewOff', { date: fmtDate(data.currentPeriodEnd) })}
          </p>
        )}
        {code === 'FREE' && (
          <p className="text-[13px] text-surface-500">{t('freeNoExpiry')}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {code === 'FREE' && (
          <>
            <Button onClick={() => handleUpgrade('PRO')}>{t('upgrade.pro', { price: '149,000đ' })}</Button>
            <Button variant="outline" onClick={() => handleUpgrade('BUSINESS')}>
              {t('upgrade.business', { price: '399,000đ' })}
            </Button>
          </>
        )}
        {code === 'PRO' && (
          <>
            <Button onClick={() => handleUpgrade('BUSINESS')}>{t('upgrade.business', { price: '399,000đ' })}</Button>
            {data.autoRenew ? (
              <Button variant="outline" onClick={() => cancel.mutate()}>{t('cancel')}</Button>
            ) : (
              <Button variant="outline" onClick={() => resume.mutate()}>{t('resume')}</Button>
            )}
          </>
        )}
        {code === 'BUSINESS' && (
          data.autoRenew ? (
            <Button variant="outline" onClick={() => cancel.mutate()}>{t('cancel')}</Button>
          ) : (
            <Button variant="outline" onClick={() => resume.mutate()}>{t('resume')}</Button>
          )
        )}
      </div>
    </div>
  );
}
